import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Trash2, HelpCircle } from "lucide-react";

import { registerConfirmHost } from "@/lib/confirm";
import { haptic } from "@/lib/haptics";
import { SheetRoot, SheetShell, SheetTitle, SheetDescription } from "@/components/mobile/SheetShell";

/**
 * Renders the confirmation sheet behind lib/confirm → confirmAction().
 * Mount once, near the root of the app.
 */
export default function ConfirmHost() {
  const { t } = useTranslation();
  const [state, setState] = useState({ open: false, options: {} });
  const resolver = useRef(null);

  const ask = useCallback(
    (options) =>
      new Promise((resolve) => {
        // A pending question is answered "no" if another one replaces it.
        resolver.current?.(false);
        resolver.current = resolve;
        setState({ open: true, options });
        haptic("light");
      }),
    []
  );

  useEffect(() => registerConfirmHost(ask), [ask]);

  const settle = (answer) => {
    resolver.current?.(answer);
    resolver.current = null;
    setState((s) => ({ ...s, open: false }));
  };

  const { title, message, confirmLabel, cancelLabel, destructive } = state.options;
  const Icon = destructive ? Trash2 : HelpCircle;

  return (
    <SheetRoot
      open={state.open}
      onOpenChange={(o) => {
        if (!o) settle(false);
      }}
    >
      <SheetShell showClose={false} data-testid="confirm-sheet" bodyClassName="px-5 pt-2">
        <div className="flex flex-col items-center text-center">
          <div
            className={`mb-3 grid h-12 w-12 place-items-center rounded-full ${
              destructive ? "bg-red-50 text-red-600" : "bg-stone-100 text-neutral-700"
            }`}
          >
            <Icon className="h-5 w-5" strokeWidth={1.75} />
          </div>
          <SheetTitle className="font-display text-xl font-black tracking-tight">
            {title || message || t("common.confirm")}
          </SheetTitle>
          <SheetDescription className="mt-1.5 text-sm leading-relaxed text-neutral-600">
            {title ? message : ""}
          </SheetDescription>
        </div>
        <div className="mt-6 flex flex-col gap-2.5">
          <button
            type="button"
            data-testid="confirm-sheet-yes"
            onClick={() => {
              haptic(destructive ? "medium" : "light");
              settle(true);
            }}
            className={`h-12 rounded-xl text-[15px] font-semibold text-white ${
              destructive ? "bg-red-600 active:bg-red-700" : "bg-neutral-950 active:bg-neutral-800"
            }`}
          >
            {confirmLabel || (destructive ? t("common.delete") : t("common.confirm"))}
          </button>
          <button
            type="button"
            data-testid="confirm-sheet-no"
            onClick={() => settle(false)}
            className="h-12 rounded-xl bg-stone-100 text-[15px] font-semibold text-neutral-900 active:bg-stone-200"
          >
            {cancelLabel || t("common.cancel")}
          </button>
        </div>
      </SheetShell>
    </SheetRoot>
  );
}
